import React, { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { format, isBefore, startOfDay } from "date-fns";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import { Button } from "./ui/button";
import "../styles/GenerateInvoiceNew.css";
import { ChevronRight, Download, Info, Save, Sparkles } from "lucide-react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import CustomModal from "./CustomModal";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import Step1InvoiceDetails from "./Step1InvoiceDetails";
import Step2ReviewLines from "./Step2ReviewLines";
import Step3PreviewGenerate from "./Step3PreviewGenerate";
import { PERMISSIONS } from "../utils/rbac";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
import { Textarea } from "./ui/textarea";
import { parseLocalDate } from "../utils/parseLocalDate";
import { TAX_RATE } from "../config/invoiceConfig";

const apiUrl = process.env.REACT_APP_API_URL;

function isEpochishDate(d) {
  if (!(d instanceof Date)) return false;
  const t = d.getTime();
  if (!Number.isFinite(t)) return false;
  // Treat Unix epoch (and near-epoch timezone shifts) as "empty" for this UI.
  return t >= -86400000 && t <= 86400000;
}

function normalizeInvoiceDateInput(v) {
  const d = v instanceof Date ? v : parseLocalDate(v);
  if (!(d instanceof Date)) return null;
  if (!Number.isFinite(d.getTime())) return null;
  if (isEpochishDate(d)) return null;
  return d;
}

function formatDateOrEmptyString(d) {
  return d instanceof Date && Number.isFinite(d.getTime())
    ? format(d, "yyyy-MM-dd")
    : "";
}

const safeToast = {
  success: (m) => {
    try {
      toast.success(m);
    } catch (e) {
      console.log(m);
    }
  },
  error: (m) => {
    try {
      toast.error(m);
    } catch (e) {
      console.log(m);
    }
  },
  message: (m) => {
    try {
      toast.message(m);
    } catch (e) {
      console.log(m);
    }
  },
};

function money(n) {
  const num = Number(n) || 0;
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function GenerateInvoiceNew() {
  const location = useLocation();
  const navigate = useNavigate();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canEditInvoice = isAllowed(PERMISSIONS.EDIT);
  const canCreate = isAllowed(PERMISSIONS.CREATE_INVOICE);
  const canPdf = isAllowed(PERMISSIONS.GENERATE_PDF);
  const step3PdfGenerateRef = useRef(null);
  const createDialogShown = useRef(false);
  const [showPreview, setShowPreview] = useState(false);
  const [property, setProperty] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const draftKeyInvoiceNumberRef = useRef("");
  /** DB primary key for this invoice (set when editing via URL or after first draft save). */
  const invoiceDbIdRef = useRef(null);
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState(() => [
    {
      id: crypto.randomUUID(),
      employeeId: "",
      name: "",
      role: "",
      hours: "",
      minutes: "0",
      rate: "",
      isNew: true,
    },
  ]);

  const [downloadPdf, setDownloadPdf] = useState(false);
  const [emailClient, setEmailClient] = useState(false);
  const [clientEmail, setClientEmail] = useState("");

  const [errors, setErrors] = useState({ step1: {}, step2: {}, step3: {} });
  const [attempted, setAttempted] = useState({ 1: false, 2: false, 3: false });
  const [touched, setTouched] = useState({
    property: false,
    invoiceNumber: false,
    startDate: false,
    endDate: false,
    invoiceDate: false,
  });
  const [emailTouched, setEmailTouched] = useState(false);

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeFetchError, setEmployeeFetchError] = useState(null);
  const [propertiesLoadError, setPropertiesLoadError] = useState(null);
  const [prefillFound, setPrefillFound] = useState(false);

  const [employeeById, setEmployeeById] = useState({});

  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaveError, setDraftSaveError] = useState(null);
  const lastDraftKeyRef = useRef("");

  const [generateLoading, setGenerateLoading] = useState(false);
  const [saveDraftLoading, setSaveDraftLoading] = useState(false);

  const invoiceIdQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const v = params.get("invoiceId");
    if (!v) return "";
    const n = parseInt(String(v).trim(), 10);
    return Number.isFinite(n) && n > 0 ? String(n) : "";
  }, [location.search]);

  useEffect(() => {
    if (invoiceIdQuery) return;
    setInvoiceDate((d) => d || new Date());
  }, [invoiceIdQuery]);

  const forceStepQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const v = params.get("forceStep");
    return v ? parseInt(String(v), 10) : null;
  }, [location.search]);

  const isPreviewMode = forceStepQuery === 3;

  useEffect(() => {
    if (!invoiceIdQuery || canEditInvoice) return;
    if (forceStepQuery !== 3) {
      navigate(
        `/generateInvoiceNew?invoiceId=${encodeURIComponent(invoiceIdQuery)}&forceStep=3`,
        { replace: true },
      );
    }
  }, [invoiceIdQuery, canEditInvoice, forceStepQuery, navigate]);

  useEffect(() => {
    if (invoiceIdQuery || canCreate || createDialogShown.current) return;
    createDialogShown.current = true;
    guardAction(PERMISSIONS.CREATE_INVOICE, "generate invoice");
  }, [invoiceIdQuery, canCreate, guardAction]);

  const autoPdfQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("autoPdf") === "1";
  }, [location.search]);

  useEffect(() => {
    if (!apiUrl) return;
    if (!invoiceIdQuery) return;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/invoices/${invoiceIdQuery}`);
        if (cancelled) return;
        const invoice = res?.data?.data?.invoice;
        const lines = Array.isArray(res?.data?.data?.lines)
          ? res.data.data.lines
          : [];
        if (!invoice) return;

        invoiceDbIdRef.current = invoice.id != null ? Number(invoice.id) : null;
        setInvoiceNumber(String(invoice.invoice_number || ""));
        setInvoiceStatus(String(invoice.status || ""));
        draftKeyInvoiceNumberRef.current = String(
          invoice.invoice_number || "",
        ).trim();
        setProperty(String(invoice.property_name || ""));
        setStartDate(normalizeInvoiceDateInput(invoice.start_date));
        setEndDate(normalizeInvoiceDateInput(invoice.end_date));
        setInvoiceDate(parseLocalDate(invoice.invoice_date));
        setDueDate(parseLocalDate(invoice.due_date));
        setClientEmail(String(invoice.client_email || ""));
        setDownloadPdf(!!invoice.download_pdf);
        setEmailClient(!!invoice.email_client);
        setNotes(String(invoice.notes || ""));

        const nextRows = (lines || []).map((l) => ({
          id: crypto.randomUUID(),
          employeeId: l.employee_id != null ? String(l.employee_id) : "",
          name: l.employee_name || "",
          role: l.role || "",
          hours: l.hours != null ? String(l.hours) : "",
          minutes: l.minutes != null ? String(l.minutes) : "0",
          rate: l.rate != null ? String(l.rate) : "",
          isNew: false,
        }));

        setRows(nextRows);

        if (forceStepQuery === 3) {
          setShowPreview(true);
          return;
        }
      } catch (e) {}
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, invoiceIdQuery, forceStepQuery]);

  const fetchNextNumber = async (prop) => {
    if (invoiceIdQuery || !apiUrl) return;
    try {
      const res = await axios.get(`${apiUrl}/api/invoices/next-number`, {
        params: { propertyName: prop },
      });
      if (res.data?.status === 1 && res.data?.nextInvoiceNumber) {
        setInvoiceNumber(res.data.nextInvoiceNumber);
      }
    } catch (e) {
      console.error("Error fetching next invoice number:", e);
    }
  };

  // Prefill employees when property changes
  useEffect(() => {
    if (invoiceIdQuery || !apiUrl) return;

    if (!property) {
      setInvoiceNumber("");
      setNotes("");
      setPrefillFound(false);
      setRows([]);
      return;
    }

    const fetchPrefill = async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/invoices/prefill`, {
          params: { propertyName: property },
        });

        // Always reset rows to a clean state before prefilling
        const defaultRow = {
          id: crypto.randomUUID(),
          employeeId: "",
          name: "",
          role: "",
          hours: "",
          minutes: "0",
          rate: "",
          isNew: true,
        };

        if (res.data?.status === 1) {
          // Explicitly do NOT prefill notes anymore as per user request
          setNotes("");

          if (res.data.employees?.length > 0) {
            setPrefillFound(true);
            const prefilledRows = res.data.employees.map((emp) => ({
              id: crypto.randomUUID(),
              employeeId: emp.employeeId != null ? String(emp.employeeId) : "",
              name: emp.name || "",
              role: emp.role || "",
              hours: emp.hours != null ? String(emp.hours) : "",
              minutes: emp.minutes != null ? String(emp.minutes) : "0",
              rate: emp.rate != null ? String(emp.rate) : "",
              isNew: false,
            }));

            setRows(prefilledRows);
            toast.success("Employees and notes prefilled from latest invoice");
          } else {
            // No previous employees, just reset to default row
            setPrefillFound(false);
            setRows([defaultRow]);
          }
        } else {
          // Status not 1, reset just in case
          setPrefillFound(false);
          setRows([defaultRow]);
          setNotes("");
        }
      } catch (e) {
        console.error("Error fetching prefill data:", e);
      }
    };

    fetchPrefill();
    fetchNextNumber(property);
  }, [apiUrl, property, invoiceIdQuery]);

  const buildDraftPayload = ({ status = null } = {}) => {
    const keyInvoiceNumber = draftKeyInvoiceNumberRef.current || invoiceNumber;
    return {
      invoiceId:
        invoiceDbIdRef.current != null ? invoiceDbIdRef.current : undefined,
      invoiceNumber: keyInvoiceNumber,
      property,
      // Use empty string for empty dates so backend doesn't coerce null/invalid to epoch.
      startDate: formatDateOrEmptyString(startDate),
      endDate: formatDateOrEmptyString(endDate),
      invoiceDate:
        invoiceDate instanceof Date ? format(invoiceDate, "yyyy-MM-dd") : null,
      dueDate: dueDate instanceof Date ? format(dueDate, "yyyy-MM-dd") : null,
      downloadPdf,
      emailClient,
      clientEmail,
      notes,
      draftStep: showPreview ? 3 : 1,
      rows,
      status,
    };
  };

  const saveDraft = async ({ immediate = false, status = null } = {}) => {
    if (!apiUrl) return;
    if (!invoiceNumber || String(invoiceNumber).trim().length === 0) return;

    const payload = buildDraftPayload({ status });
    const draftKey = JSON.stringify(payload);
    if (!immediate && lastDraftKeyRef.current === draftKey) return;

    try {
      setDraftSaving(true);
      setDraftSaveError(null);
      const res = await axios.post(`${apiUrl}/api/invoices/draft`, payload);
      lastDraftKeyRef.current = draftKey;
      const newId = res?.data?.data?.id;
      if (newId != null) {
        invoiceDbIdRef.current = Number(newId);
      }
      return res?.data;
    } catch (e) {
      setDraftSaveError("Failed to save draft");
      throw e;
    } finally {
      setDraftSaving(false);
    }
  };

  useEffect(() => {
    if (emailClient) return;
    if (!emailTouched) return;
    setEmailTouched(false);
  }, [emailClient, emailTouched]);

  useEffect(() => {
    if (!apiUrl) return;

    let cancelled = false;
    const fetchEmployeesAndProperties = async () => {
      try {
        setEmployeeFetchError(null);
        const payload = { eligibilityFilter: "invoice" };
        if (startDate instanceof Date)
          payload.payFromDate = format(startDate, "yyyy-MM-dd");
        if (endDate instanceof Date)
          payload.payToDate = format(endDate, "yyyy-MM-dd");

        const response = await axios.post(
          `${apiUrl}/getAllUsersForEmployeeInvoice`,
          payload,
        );

        if (cancelled) return;

        const rawEmployees = Array.isArray(response?.data?.employees)
          ? response.data.employees
          : [];
        // Do NOT filter by hasInvoice here — that flag is populated from the
        // payslip table (employeeInvoice), not the company invoice table.
        // Filtering it would incorrectly hide invoice-eligible employees who
        // happen to have a payslip for the same date range (the intermittent bug).
        const filteredEmployees = rawEmployees;

        setEmployeeById(
          filteredEmployees.reduce((acc, e) => {
            const id = e?.id != null ? String(e.id) : null;
            if (!id) return acc;
            acc[id] = e;
            return acc;
          }, {}),
        );
        setEmployeeOptions(
          filteredEmployees.map((e) => ({
            value: String(e.id),
            label: e.name,
          })),
        );

        const rawProperties = Array.isArray(response?.data?.propertyNames)
          ? response.data.propertyNames
          : [];
        const nextProps = rawProperties
          .map((p) => {
            const name = p?.NAME ?? p?.name;
            if (!name) return null;
            const v = String(name);
            return { value: v, label: v };
          })
          .filter(Boolean);
        setPropertyOptions((prev) => {
          const map = new Map();
          (prev || []).forEach((o) => map.set(o.value, o));
          nextProps.forEach((o) => map.set(o.value, o));
          return Array.from(map.values());
        });
      } catch (e) {
        if (cancelled) return;
        setEmployeeFetchError("Failed to load employees/properties");
      }
    };

    fetchEmployeesAndProperties();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    if (!apiUrl) return;
    let cancelled = false;

    const fetchProperties = async () => {
      try {
        setPropertiesLoadError(null);
        const res = await axios.get(`${apiUrl}/getPropertiesAndDeductions`);
        if (cancelled) return;
        const raw = Array.isArray(res?.data?.properties)
          ? res.data.properties
          : [];
        const opts = raw
          .map((p) => {
            const name = p?.NAME ?? p?.name;
            if (!name) return null;
            const v = String(name);
            return { value: v, label: v };
          })
          .filter(Boolean);

        setPropertyOptions((prev) => {
          const map = new Map();
          (prev || []).forEach((o) => map.set(o.value, o));
          opts.forEach((o) => map.set(o.value, o));
          return Array.from(map.values());
        });
      } catch (e) {
        if (cancelled) return;
        setPropertiesLoadError("Failed to load properties");
      }
    };

    fetchProperties();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Validation ---

  const validateStep1 = () => {
    const step1Schema = z
      .object({
        property: z.string().trim().min(1, "Property is required"),
        invoiceNumber: z
          .string()
          .trim()
          .min(1, "Invoice number is required")
          .max(64, "Invoice number is too long")
          .refine(
            (s) => /^[A-Za-z0-9\-_]+$/.test(s),
            "Use letters, numbers, hyphens, or underscores only",
          ),
        startDate: z
          .date()
          .nullable()
          .refine((v) => v instanceof Date, "Start date is required"),
        endDate: z
          .date()
          .nullable()
          .refine((v) => v instanceof Date, "End date is required"),
        invoiceDate: z
          .date()
          .nullable()
          .refine((v) => v instanceof Date, "Invoice date is required"),
        dueDate: z.date().nullable().optional(),
      })
      .superRefine((v, ctx) => {
        if (v.startDate && v.endDate && isBefore(v.endDate, v.startDate)) {
          ctx.addIssue({
            code: "custom",
            path: ["endDate"],
            message: "End date must be on or after start date",
          });
        }
        if (
          v.dueDate &&
          v.invoiceDate &&
          isBefore(startOfDay(v.dueDate), startOfDay(v.invoiceDate))
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["dueDate"],
            message: "Due date must be on or after invoice date",
          });
        }
      });

    const r = step1Schema.safeParse({
      property,
      invoiceNumber,
      startDate,
      endDate,
      invoiceDate,
      dueDate,
    });
    if (!r.success) {
      const next = {};
      r.error.issues.forEach((i) => {
        next[i.path[0]] = i.message;
      });
      setErrors((e) => ({ ...e, step1: next }));
      return false;
    }
    setErrors((e) => ({ ...e, step1: {} }));
    return true;
  };

  // Auto-validate step 1 on change
  useEffect(() => {
    const hasStarted = property || invoiceNumber || startDate || endDate;
    if (hasStarted) {
      setAttempted((a) => ({ ...a, 1: true }));
      validateStep1();
    }
  }, [property, invoiceNumber, startDate, endDate, invoiceDate]);

  const validateStep2 = () => {
    const rowSchema = z
      .object({
        employeeId: z.string().trim().min(1, "Employee must be selected"),
        name: z.string().trim().min(1, "Employee name is required"),
        hours: z
          .string()
          .trim()
          .optional()
          .refine((s) => {
            const v = String(s || "").trim();
            if (!v) return true;
            return !Number.isNaN(parseFloat(v));
          }, "Hours must be a number")
          .refine((s) => {
            const v = String(s || "").trim();
            if (!v) return true;
            return parseFloat(v) >= 0;
          }, "Hours must be 0 or greater"),
        minutes: z
          .string()
          .trim()
          .optional()
          .refine((s) => {
            const v = String(s || "").trim();
            if (!v) return true;
            const n = parseInt(v, 10);
            return !Number.isNaN(n) && n >= 0;
          }, "Minutes must be 0 or greater"),
        rate: z
          .string()
          .trim()
          .min(1, "Rate is required")
          .refine((s) => !Number.isNaN(parseFloat(s)), "Rate must be a number")
          .refine((s) => parseFloat(s) > 0, "Rate must be greater than 0"),
      })
      .superRefine((v, ctx) => {
        const hs = String(v.hours || "").trim();
        const ms = String(v.minutes || "").trim();
        const h = hs ? parseFloat(hs) : 0;
        const m = ms ? parseInt(ms, 10) : 0;

        const hasAny = !!hs || !!ms;
        const total =
          (Number.isFinite(h) ? h : 0) + (Number.isFinite(m) ? m : 0) / 60;

        if (!hasAny) {
          ctx.addIssue({
            code: "custom",
            path: ["hours"],
            message: "Hrs or mins required",
          });
          return;
        }
        if (!(total > 0)) {
          ctx.addIssue({
            code: "custom",
            path: ["hours"],
            message: "Hrs or mins must greater than 0",
          });
        }
      });

    const step2Schema = z.object({
      rows: z.array(rowSchema).min(1, "Add at least 1 line item"),
    });

    const r = step2Schema.safeParse({ rows });
    if (!r.success) {
      const next = { rows: {}, form: undefined };
      r.error.issues.forEach((i) => {
        // Path matches ["rows", index, field]
        if (
          i.path?.[0] === "rows" &&
          typeof i.path?.[1] === "number" &&
          i.path?.[2]
        ) {
          const idx = i.path[1];
          const field = i.path[2];
          next.rows[idx] = next.rows[idx] || {};
          next.rows[idx][field] = i.message;
        } else if (i.path?.[0] === "rows" && typeof i.path?.[1] === "number") {
          // Path matches ["rows", index] - map to a generic field if needed, but rowSchema issues have paths
          const idx = i.path[1];
          next.rows[idx] = next.rows[idx] || {};
          next.rows[idx].form = i.message;
        } else {
          // Top level error (like min(1))
          next.form = i.message;
        }
      });
      setErrors((e) => ({ ...e, step2: next }));
      return false;
    }
    setErrors((e) => ({ ...e, step2: {} }));
    return true;
  };

  const isStep1Complete = useMemo(() => {
    // Check if step 1 is valid without updating error state
    const step1Schema = z
      .object({
        property: z.string().trim().min(1),
        invoiceNumber: z
          .string()
          .trim()
          .min(1)
          .max(64)
          .refine((s) => /^[A-Za-z0-9\-_]+$/.test(s)),
        startDate: z.date(),
        endDate: z.date(),
        invoiceDate: z.date(),
      })
      .superRefine((v, ctx) => {
        if (v.startDate && v.endDate && isBefore(v.endDate, v.startDate)) {
          ctx.addIssue({ code: "custom", path: ["endDate"] });
        }
      });
    const r = step1Schema.safeParse({
      property,
      invoiceNumber,
      startDate,
      endDate,
      invoiceDate,
    });
    return r.success;
  }, [property, invoiceNumber, startDate, endDate, invoiceDate]);

  const isStep2Complete = useMemo(() => {
    return rows.length > 0;
  }, [rows]);

  const onValidateStep3 = () => {
    setAttempted((a) => ({ ...a, 3: true }));

    const step3Schema = z
      .object({
        downloadPdf: z.boolean(),
        emailClient: z.boolean(),
        clientEmail: z.string().trim().optional(),
      })
      .superRefine((v, ctx) => {
        if (v.emailClient) {
          const email = z.string().email("Enter a valid email");
          const r = email.safeParse(v.clientEmail || "");
          if (!r.success) {
            ctx.addIssue({
              code: "custom",
              path: ["clientEmail"],
              message: "Enter a valid email",
            });
          }
        }
      });

    const r = step3Schema.safeParse({ downloadPdf, emailClient, clientEmail });
    if (!r.success) {
      const next = {};
      r.error.issues.forEach((i) => {
        next[i.path[0]] = i.message;
      });
      setErrors((e) => ({ ...e, step3: next }));
      return false;
    }
    setErrors((e) => ({ ...e, step3: {} }));
    return true;
  };

  // --- Totals ---

  const totals = useMemo(() => {
    const parsed = rows
      .filter((r) => !r?.isNew)
      .map((r) => {
        const h = parseInt(r.hours, 10) || 0;
        const m = parseInt(r.minutes, 10) || 0;
        const totalHours = h + m / 60;
        const rate = parseFloat(r.rate) || 0;
        return { h: totalHours, amt: totalHours * rate };
      });
    const subtotal = parsed.reduce((a, b) => a + b.amt, 0);
    const taxRate = TAX_RATE;
    const tax = subtotal * taxRate;
    const total = subtotal + tax;
    return { subtotal, tax, total, taxRate };
  }, [rows]);

  // --- Actions ---

  const handleGeneratePreview = async () => {
    // When clicking Generate, everything should be marked as touched
    setTouched({
      property: true,
      invoiceNumber: true,
      startDate: true,
      endDate: true,
      invoiceDate: true,
    });
    setAttempted({ 1: true, 2: true, 3: true });

    const ok1 = validateStep1();
    const ok2 = validateStep2();
    const ok3 = onValidateStep3();
    if (!ok1 || !ok2 || !ok3) {
      safeToast.error("Please fix the errors before continuing");
      return;
    }

    setGenerateLoading(true);
    const previewData = {
      property,
      invoiceNumber,
      invoiceDate,
      startDate,
      endDate,
      dueDate,
      rows,
      notes,
      downloadPdf,
      emailClient,
      clientEmail,
    };

    try {
      await saveDraft({ immediate: true, status: "SENT" });
      navigate("/previewInvoice", { state: previewData });
    } catch (e) {
      safeToast.error("Failed to transition invoice status. Please try again.");
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setTouched({
      property: true,
      invoiceNumber: true,
      startDate: true,
      endDate: true,
      invoiceDate: true,
    });
    setAttempted({ 1: true, 2: true, 3: true });

    const ok1 = validateStep1();
    const ok2 = validateStep2();
    const ok3 = onValidateStep3();

    if (!ok1 || !ok2 || !ok3) {
      safeToast.error("Please fix errors before saving draft");
      return;
    }

    setSaveDraftLoading(true);
    try {
      await saveDraft({ immediate: true });
      safeToast.success("Invoice saved as draft");
    } catch (e) {
      safeToast.error("Failed to save draft");
    } finally {
      setSaveDraftLoading(false);
    }
  };

  const onGenerate = () => {
    const run = async () => {
      const ok = onValidateStep3();
      if (!ok) return;
      try {
        await saveDraft({ immediate: true, status: "SENT" });
        if (downloadPdf) {
          if (canPdf) {
            safeToast.message("Saved successfully. Generating PDF...");
            if (typeof step3PdfGenerateRef.current === "function") {
              await step3PdfGenerateRef.current();
            }
          } else {
            safeToast.success("Invoice saved successfully");
          }
          setSuccessOpen(true);
        } else {
          safeToast.success("Saved successfully");
          setSuccessOpen(true);
        }
      } catch (e) {
        setDraftSaveError("Failed to mark invoice as SENT");
      }
    };

    run();
  };

  const step3ErrorsForDisplay = useMemo(() => {
    const e = errors.step3 || {};
    return {
      ...e,
      clientEmail:
        emailClient && (emailTouched || attempted[3])
          ? e.clientEmail
          : undefined,
      delivery: attempted[3] ? e.delivery : undefined,
    };
  }, [errors.step3, emailClient, emailTouched, attempted]);

  const isEditing = !!invoiceIdQuery;
  const isViewOnly = isEditing && !canEditInvoice;
  const normalizedInvoiceStatus = String(invoiceStatus || "")
    .trim()
    .toLowerCase();
  const isDraftEdit = isEditing && normalizedInvoiceStatus === "draft";

  // If in preview mode (from URL or after clicking Generate & Preview)
  if (showPreview || isPreviewMode) {
    return (
      <div className="admin-panel-parent-container invoices-page-wrapper">
        <NavBar />
        <TopNavBar />
        <PermissionDialog />

        <div className="invoices-page-wrapper ml-[15%] mt-[66px] px-4 sm:px-6 lg:px-8 py-12 sm:py-6 bg-gray-100 min-h-[calc(100vh-66px)]">
          {/* Breadcrumbs */}
          <div className="text-sm mb-4 flex flex-wrap items-center gap-y-1">
            <Link className="text-primary hover:underline" to="/dashboard">
              Dashboard
            </Link>
            <span className="text-primary mx-2">&gt;</span>
            <Link className="text-primary hover:underline" to="/invoices">
              Invoices
            </Link>
            <span className="text-primary mx-2">&gt;</span>
            <span className="text-primary font-bold">
              {isViewOnly ? "View Invoice" : "Preview Invoice"}
            </span>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-2xl sm:text-3xl font-black text-[#131614]">
                {isViewOnly ? "View Invoice" : "Preview Invoice"}
              </span>
              <p className="text-sm text-[#64748B] mt-1">
                {isViewOnly
                  ? "Read-only invoice details"
                  : "Review your document before downloading or sending"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="default"
                className={deniedActionClass(canPdf)}
                onClick={() => {
                  guardAction(PERMISSIONS.GENERATE_PDF, "download PDF", () => {
                    if (typeof step3PdfGenerateRef.current === "function") {
                      step3PdfGenerateRef.current();
                    }
                  });
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              {!isPreviewMode && canEditInvoice && (
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  ← Back to Edit
                </Button>
              )}
            </div>
          </div>

          <Step3PreviewGenerate
            property={property}
            invoiceNumber={invoiceNumber}
            invoiceDate={invoiceDate}
            startDate={startDate}
            endDate={endDate}
            dueDate={dueDate}
            rows={rows}
            downloadPdf={downloadPdf}
            setDownloadPdf={setDownloadPdf}
            emailClient={emailClient}
            setEmailClient={setEmailClient}
            clientEmail={clientEmail}
            setClientEmail={setClientEmail}
            notes={notes}
            errors={step3ErrorsForDisplay}
            onValidate={onValidateStep3}
            onGenerate={onGenerate}
            onEmailTouched={() => setEmailTouched(true)}
            autoGeneratePdf={autoPdfQuery}
            pdfFileName={`invoice-${
              String(invoiceNumber || "")
                .replace("#", "")
                .trim() || "draft"
            }.pdf`}
            onPdfGenerated={() => {}}
            isPreviewMode={isPreviewMode}
            refPdfGenerate={(fn) => {
              step3PdfGenerateRef.current = fn;
            }}
          />

          {!isPreviewMode ? (
            <CustomModal open={successOpen} onOpenChange={setSuccessOpen}>
              <div className="p-8">
                <div className="flex justify-center">
                  <div className="h-16 w-16 rounded-full bg-[#2F7F571A] flex items-center justify-center">
                    <div className="h-7 w-7 rounded-full bg-[#2F7F57] flex items-center justify-center text-white text-sm font-black">
                      ✓
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-center text-xl font-black text-[#131614]">
                  Invoice created successfully
                </div>
                <div className="mt-2 text-center text-sm text-[#6B7280]">
                  The invoice for {property || "—"} has been generated and saved
                  to your records.
                </div>

                <div className="mt-6 space-y-3">
                  <Button
                    className="w-full"
                    onClick={() => {
                      window.location.href = "/invoices";
                    }}
                  >
                    Go to Invoice List
                  </Button>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      const id = invoiceDbIdRef.current;
                      if (!id) {
                        setSuccessOpen(false);
                        return;
                      }
                      window.location.href = `/generateInvoiceNew?invoiceId=${encodeURIComponent(String(id))}&forceStep=3&preview=1`;
                    }}
                  >
                    View Invoice
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full text-[#6B7280] hover:text-[#131614]"
                    onClick={() => setSuccessOpen(false)}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </CustomModal>
          ) : null}
        </div>
      </div>
    );
  }

  const createBlocked = !invoiceIdQuery && !canCreate;

  // --- Main Page: All 3 steps on one page ---
  return (
    <div className="admin-panel-parent-container invoices-page-wrapper">
      <NavBar />
      <TopNavBar />
      <PermissionDialog />

      <div
        className={`invoices-page-wrapper ml-[15%] mt-[66px] px-4 sm:px-6 lg:px-8 py-12 sm:py-6 bg-gray-100 min-h-[calc(100vh-66px)] ${createBlocked ? "pointer-events-none opacity-60" : ""}`}
      >
        {/* Breadcrumbs */}
        <div className="text-sm mb-4 flex flex-wrap items-center gap-y-1">
          <Link className="text-primary hover:underline" to="/dashboard">
            Dashboard
          </Link>
          <span className="text-primary mx-2">&gt;</span>
          <Link className="text-primary hover:underline" to="/invoices">
            Invoices
          </Link>
          <span className="text-primary mx-2">&gt;</span>
          <span className="text-primary font-bold">
            {isEditing ? "Edit Smart Invoice" : "Generate Invoice"}
          </span>
        </div>

        {/* Page Header */}
        <div className="flex items-center justify-between mb-6 pt-1">
          <div>
            <span className="text-2xl sm:text-3xl font-black text-[#131614]">
              {isEditing ? "Edit Smart Invoice" : "Create Smart Invoice"}
            </span>
            <p className="text-sm text-[#64748B] mt-1">
              Guided step-by-step invoice generation workflow
            </p>
          </div>
          {isEditing && (
            <Badge
              variant="outline"
              className="border-[#2F7F571A] text-[#2F7F57] bg-[#2F7F571A] font-bold text-xs px-3 py-1"
            >
              DRAFT STATUS
            </Badge>
          )}
        </div>

        {/* All steps stacked vertically */}
        <div className="space-y-6">
          {/* Step 1: Property & Period */}
          <Step1InvoiceDetails
            isEditing={isEditing}
            property={property}
            setProperty={setProperty}
            propertyOptions={propertyOptions}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            invoiceDate={invoiceDate}
            setInvoiceDate={setInvoiceDate}
            dueDate={dueDate}
            setDueDate={setDueDate}
            invoiceNumber={invoiceNumber}
            setInvoiceNumber={setInvoiceNumber}
            errors={errors.step1}
            touched={touched}
            setTouched={setTouched}
            prefillFound={prefillFound}
          />

          {/* Step 2: Line Items Review - visible if property is selected */}
          {property && (
            <Step2ReviewLines
              rows={rows}
              setRows={setRows}
              errors={attempted[2] ? errors.step2 : {}}
              employeeOptions={employeeOptions}
              employeeFetchError={employeeFetchError}
              employeeById={employeeById}
              onRowsChanged={() => saveDraft({ immediate: true })}
            />
          )}

          {/* Step 3: Final Totals & Generation - visible if property is selected */}
          {property && (
            <Card className="border-[#2F7F571A] shadow-[0px_1px_2px_0px_#0000000D] overflow-hidden">
              <div className="p-6 bg-[#2F7F570D] border-b border-[#2F7F570D] border">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-md bg-[#2F7F57] flex items-center justify-center text-white text-base font-bold flex-shrink-0 mt-0.5">
                    3
                  </div>
                  <div>
                    <div className="text-md font-bold text-[#131614]">
                      Final Totals & Generation
                    </div>
                    <div className="text-xs text-[#6B7280]">
                      Summary of all charges and internal notes
                    </div>
                  </div>
                </div>
              </div>

              <CardContent className="pt-6">
                <div className="grid grid-cols-12 gap-12">
                  {/* Left: Notes */}
                  <div className="col-span-12 lg:col-span-6">
                    <div className="text-sm font-medium text-[#131614] mb-2">
                      Invoice Notes (Internal)
                    </div>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any special instructions or billing notes here..."
                      className="w-full min-h-[120px] resize-none bg-transparent"
                    />

                    {/* Warning box */}
                    <div className="mt-5 rounded-lg border border-[#F59E0B33] bg-[#FFFBEB] p-3 flex items-start gap-2">
                      <Info className="h-4 w-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-[#92400E]">
                        Finalizing this invoice will lock the hours for the
                        selected period and send it to the review queue.
                      </div>
                    </div>
                  </div>

                  {/* Right: Totals + Actions */}
                  <div className="col-span-12 lg:col-span-6">
                    <div className="rounded-xl border border-[#2F7F571A] bg-[#2F7F570D] p-5">
                      <div className="flex items-center justify-between text-sm text-[#475569]">
                        <span>Subtotal</span>
                        <span className="font-medium ">
                          {money(totals.subtotal)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm  mt-2">
                        <span>Tax ({(totals.taxRate * 100).toFixed(0)}%)</span>
                        <span className="font-medium ">
                          {money(totals.tax)}
                        </span>
                      </div>

                      <div className="h-px bg-[#2F7F571A] my-4" />

                      <div className="flex flex-col items-start gap-2">
                        <div className="text-xs font-bold tracking-widest text-[#2F7F57]">
                          GRAND TOTAL
                        </div>
                        <div className="text-3xl font-black text-[#131614]">
                          {money(totals.total)}
                        </div>
                      </div>
                      <div className="flex  flex-col">
                        <Button
                          className="md:w-full w-fit mt-5"
                          type="button"
                          disabled={generateLoading}
                          onClick={handleGeneratePreview}
                        >
                          {generateLoading ? (
                            <>
                              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-1 font-bold" />
                              {isEditing
                                ? "Update Invoice Details"
                                : "Generate & Preview"}
                            </>
                          )}
                        </Button>

                        {normalizedInvoiceStatus === "draft" && (
                          <Button
                            type="button"
                            // className="w-full mt-3 bg-[#E6F0EA] text-[#246844] hover:bg-[#D7E8DD] p-1 px-4 h-9 rounded-md text-xs font-bold transition-colors"
                            className="md:w-full w-fit mt-3 border"
                            disabled={saveDraftLoading}
                            variant="outline"
                            onClick={handleSaveDraft}
                          >
                            {saveDraftLoading ? "Saving..." : "Save as Draft"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
